import { MessageComposerFormat } from './message-composer-editor.types';

export const EMPTY_EDITOR_MARKER = '\u200b';

type ComposerElementTag = 'u' | 'em' | 'strong' | 'h3' | 'blockquote' | 'pre';

export function createFormattedComposerContent(
  document: Document,
  textNode: Text,
  activeFormats: ReadonlySet<MessageComposerFormat>,
  blockType: string,
): Node {
  let content: Node = textNode;

  if (activeFormats.has('underline')) {
    content = wrapNode(document, 'u', content);
  }
  if (activeFormats.has('italic')) {
    content = wrapNode(document, 'em', content);
  }
  if (activeFormats.has('bold')) {
    content = wrapNode(document, 'strong', content);
  }

  if (activeFormats.has('insertUnorderedList')) {
    return createList(document, 'ul', content);
  }
  if (activeFormats.has('insertOrderedList')) {
    return createList(document, 'ol', content);
  }
  if (blockType === 'h3' || blockType === 'blockquote' || blockType === 'pre') {
    return wrapNode(document, blockType, content);
  }

  return content;
}

export function findComposerMarker(editor?: HTMLElement): Text | null {
  if (!editor) return null;

  const walker = editor.ownerDocument.createTreeWalker(editor, 4);
  let node = walker.nextNode();

  while (node) {
    if (node.nodeType === 3 && (node.textContent ?? '').includes(EMPTY_EDITOR_MARKER)) {
      return node as Text;
    }
    node = walker.nextNode();
  }

  return null;
}

export function placeComposerCaretAfter(document: Document, node: Node): void {
  const range = document.createRange();
  range.setStartAfter(node);
  range.collapse(true);
  applySelection(document, range);
}

export function placeComposerCaretAfterMarker(document: Document, marker: Text | null): void {
  if (!marker) return;

  const markerIndex = marker.data.indexOf(EMPTY_EDITOR_MARKER);
  if (markerIndex < 0) return;

  const range = document.createRange();
  range.setStart(marker, markerIndex + EMPTY_EDITOR_MARKER.length);
  range.collapse(true);
  applySelection(document, range);
}

export function placeComposerCaretAtEnd(document: Document, editor: HTMLElement): void {
  const range = document.createRange();
  range.selectNodeContents(editor);
  range.collapse(false);
  applySelection(document, range);
}

export function selectComposerNodeContents(document: Document, node: Node): void {
  const range = document.createRange();
  range.selectNodeContents(node);
  applySelection(document, range);
}

export function readComposerValue(editor: HTMLElement): string {
  const textContent = normalizeComposerText(editor.textContent ?? '');
  if (!textContent.trim()) return '';

  return normalizeComposerText(editor.innerText ?? '').replace(/\n$/, '');
}

function createList(document: Document, tag: 'ul' | 'ol', content: Node): HTMLElement {
  const list = document.createElement(tag);
  const item = document.createElement('li');
  item.appendChild(content);
  list.appendChild(item);
  return list;
}

function wrapNode(document: Document, tag: ComposerElementTag, node: Node): HTMLElement {
  const wrapper = document.createElement(tag);
  wrapper.appendChild(node);
  return wrapper;
}

function normalizeComposerText(value: string): string {
  return value
    .split(EMPTY_EDITOR_MARKER)
    .join('')
    .replace(/\u00a0/g, ' ');
}

function applySelection(document: Document, range: Range): void {
  const selection = document.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}
